import { produce } from "immer";

import { AppListenerMiddleware } from "@src/store";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { ExperienceBuilderStats } from "@src/store/slices/experienceBuilder/types";
import { AppListenerApi } from "@src/store/rootState";

const { getStats } = experienceBuilderSlice.selectors;
const { setLoadState, setStats, initBuilder } = experienceBuilderSlice.actions;

export const setupStatsListener = (middleware: AppListenerMiddleware) => {
  // Anchor the session + load clocks when the builder opens. `startLoadTs` is
  // seeded here so the initial preview load (whose "ready" arrives without a
  // preceding navigation-start) measures from builder-open instead of epoch 0.
  // A real navigation-start overwrites it via the `setLoadState` branch below.
  middleware.startListening({
    actionCreator: initBuilder,
    effect: (_, api) => {
      update(api, (stats) => {
        const now = Date.now();
        stats.startSessionTs = now;
        stats.startLoadTs = now;
      });
    },
  });

  middleware.startListening({
    actionCreator: setLoadState,
    effect: (action, api) => {
      switch (action.payload?.type) {
        case undefined:
          update(api, (stats) => {
            stats.navigateCount++;
            stats.startLoadTs = Date.now();
          });
          break;
        case "ready":
          update(api, (stats) => {
            const time = Date.now() - stats.startLoadTs;
            stats.loadTimes.push(time);
          });
      }
    },
  });
};

// `getStats` returns the frozen Redux slice, so the updater mutates an Immer
// draft via `produce` rather than the frozen object directly. Keeps the
// generic "mutate the stats" ergonomics — new fields need no new reducer.
function update(
  api: AppListenerApi,
  updater: (stats: ExperienceBuilderStats) => void
) {
  const next = produce(getStats(api.getState()), updater);
  api.dispatch(setStats(next));
}
